// src/pages/finance-portal/reports/GLDetailReport.js

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid, Autocomplete,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, CircularProgress, Stack,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatDate,
    firstOfYearIso, todayIso, downloadCsv,
} from './reportShared';

export default function GLDetailReport() {
    const [searchParams] = useSearchParams();
    const urlAccountId = searchParams.get('account_id');
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');

    const [start, setStart] = useState(urlStart || firstOfYearIso());
    const [end, setEnd] = useState(urlEnd || todayIso());
    const [accounts, setAccounts] = useState([]);
    const [account, setAccount] = useState(null);
    const [reference, setReference] = useState('');
    const [minAmt, setMinAmt] = useState('');
    const [maxAmt, setMaxAmt] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rpp, setRpp] = useState(50);

    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) {
                    const list = res.data.data || [];
                    setAccounts(list);
                    if (urlAccountId) {
                        const match = list.find((a) => String(a.id) === String(urlAccountId));
                        if (match) setAccount(match);
                    }
                }
            } catch (e) { /* silent */ }
        })();
    }, [urlAccountId]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = {
                start_date: start, end_date: end,
                page: page + 1, per_page: rpp,
            };
            if (account) params.account_id = account.id;
            if (reference) params.reference = reference;
            if (minAmt) params.min_amount = minAmt;
            if (maxAmt) params.max_amount = maxAmt;
            const res = await axiosInstance.get('/api/finance/reports/gl-detail', { params });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed to load');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load GL detail');
        } finally { setLoading(false); }
    }, [start, end, account, reference, minAmt, maxAmt, page, rpp]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data || !data.lines) return;
        downloadCsv(`gl_detail_${start}_${end}.csv`, data.lines,
            ['entry_date', 'entry_number', 'account_number', 'account_name',
             'description', 'reference', 'debit', 'credit', 'running_balance']);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ArticleIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>General Ledger Detail</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={2}>
                        <TextField label="Start" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            size="small" options={accounts}
                            value={account}
                            onChange={(_, v) => setAccount(v)}
                            getOptionLabel={(a) => a ? `${a.account_number} — ${a.name}` : ''}
                            renderInput={(p) => <TextField {...p} label="Account (optional)" />}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField label="Reference" size="small" fullWidth
                            value={reference} onChange={(e) => setReference(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <TextField label="Min $" type="number" size="small" fullWidth
                            value={minAmt} onChange={(e) => setMinAmt(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={1}>
                        <TextField label="Max $" type="number" size="small" fullWidth
                            value={maxAmt} onChange={(e) => setMaxAmt(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={1} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
                        <Typography variant="body2">
                            Showing {data.lines?.length || 0} of {data.total || 0} entries
                        </Typography>
                        <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
                    </Stack>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Entry #</TableCell>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell align="right">Debit</TableCell>
                                    <TableCell align="right">Credit</TableCell>
                                    <TableCell align="right">Running</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.lines || []).map((l) => (
                                    <TableRow key={l.line_id}>
                                        <TableCell>{formatDate(l.entry_date)}</TableCell>
                                        <TableCell>{l.entry_number}</TableCell>
                                        <TableCell>{l.account_number} — {l.account_name}</TableCell>
                                        <TableCell>{l.description}</TableCell>
                                        <TableCell>{l.reference}</TableCell>
                                        <TableCell align="right">{l.debit ? formatCurrency(l.debit) : ''}</TableCell>
                                        <TableCell align="right">{l.credit ? formatCurrency(l.credit) : ''}</TableCell>
                                        <TableCell align="right">{formatCurrency(l.running_balance)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.total || 0}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rpp}
                        onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                </Paper>
            )}
        </Box>
    );
}
